import type { GetGapExecutionApiResponse } from '@momentum/types';

import { duration, tokenCount } from '../lib/format';

/**
 * How the execution actually ran, agent by agent.
 *
 * Every number here was read out of the agent's own transcript after the
 * workflow returned — not out of the model constant the workflow pinned, which
 * is the string it ASKED for rather than the one it got. That distinction is the
 * whole reason this section exists, so the model column is per agent rather than
 * a single run-level badge.
 *
 * One row per agent INVOCATION, so a retry appears as its own row (`…:retry`)
 * next to the attempt it replaced. Collapsing them would hide that the first
 * attempt burned a model's worth of tokens before dying.
 *
 * The orchestrator is deliberately absent: it decides which agents to run and
 * never reads a document, so its numbers would say nothing about the analysis.
 */
export function RunAgentsSection({
  execution,
}: {
  execution: GetGapExecutionApiResponse;
}) {
  const agents = execution.agents ?? [];

  // Executions that predate the telemetry carry none of it, and nothing
  // backfilled them. Saying so beats an empty table a reader would read as
  // "this run used no agents".
  if (agents.length === 0) {
    return (
      <section>
        <div className="gr-rule gr-rule-flush">
          <span>Agents</span>
          <i />
        </div>
        <div className="gr-empty">
          <b>No telemetry on this execution</b>
          Per-agent telemetry is collected from the agents&apos; transcripts
          after the workflow returns. Executions that ran before that existed
          were not backfilled.
        </div>
      </section>
    );
  }

  const totalOutputTokens = agents.reduce(
    (total, agent) => total + (agent.outputTokens ?? 0),
    0
  );
  const totalCompactions = agents.reduce(
    (total, agent) => total + (agent.compactions ?? 0),
    0
  );

  return (
    <section>
      <div className="gr-rule gr-rule-flush">
        <span>Agents</span>
        <i />
      </div>

      <div className="gr-agentgrid-head">
        <span className="gr-card-k">
          {agents.length} invocation{agents.length === 1 ? '' : 's'} ·{' '}
          {tokenCount(totalOutputTokens)} output tokens
          {totalCompactions > 0 && ` · ${totalCompactions} compaction(s)`}
        </span>
        <div className="gr-verdictpills">
          {(execution.models ?? []).map((model) => (
            <span className="gr-chip gr-chip-lg mono" key={model}>
              {model}
            </span>
          ))}
        </div>
      </div>

      <div className="gr-agentgrid">
        <div className="gr-agentgrid-row gr-agentgrid-row-head">
          <span>agent</span>
          <span>model</span>
          <span>effort</span>
          <span>turns</span>
          <span>output</span>
          <span>peak ctx</span>
          <span>compactions</span>
          <span>duration</span>
        </div>
        {agents.map((agent, index) => (
          // Labels repeat when the same agent runs over several batches, so the
          // index is what actually separates two invocations.
          <div className="gr-agentgrid-row" key={`${agent.label}-${index}`}>
            <span>
              <b className="mono">{agent.label}</b>
              <i className="gr-agentgrid-role">{agent.role}</i>
            </span>
            <span className="mono">{agent.model}</span>
            <span>{agent.effort ?? '—'}</span>
            <span>{agent.turns ?? '—'}</span>
            <span>{tokenCount(agent.outputTokens)}</span>
            <span>{tokenCount(agent.peakContextTokens)}</span>
            <span>{agent.compactions ?? '—'}</span>
            <span>{duration(agent.durationMs)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
